export const APPEARANCE_PREFERENCES = ["system", "light", "dark"] as const;

export type AppearancePreference = (typeof APPEARANCE_PREFERENCES)[number];

export type ResolvedAppearance = "light" | "dark";

export const UI_APPEARANCE_STORAGE_KEY = "clvabot.uiAppearance";

/**
 * Semantic palette shared by web, Electron, and Expo. Names follow the shadcn
 * convention so the same slot means the same thing on every surface: a `border`
 * is always a border and never a fill.
 */
export type ColorTokens = {
  background: string;
  /** Modals: a step lighter than the page so they read as raised above it. */
  dialog: string;
  foreground: string;
  card: string;
  cardForeground: string;
  popover: string;
  popoverForeground: string;
  /** Fill and hairline where native Liquid Glass is unavailable (Android, iOS before 26). */
  glassFill: string;
  glassStroke: string;
  primary: string;
  primaryForeground: string;
  secondary: string;
  secondaryForeground: string;
  chatUser: string;
  /** White in both themes: the user bubble is a solid fill (light grey in dark, near-black in light). */
  chatUserForeground: string;
  muted: string;
  mutedForeground: string;
  /** Tertiary text: placeholders, list markers, quote bars, timestamps. */
  faintForeground: string;
  accent: string;
  accentForeground: string;
  /** Destructive text and outlines. */
  destructive: string;
  /** Solid destructive fill (Hang up, a filled Delete); its text is `destructiveForeground`. */
  destructiveFill: string;
  destructiveForeground: string;
  border: string;
  input: string;
  ring: string;
  sidebar: string;
  sidebarForeground: string;
  sidebarBorder: string;
  sidebarAccent: string;
  sidebarAccentForeground: string;
  link: string;
  /** Backing behind a link's site icon. White in both themes so dark logos stay visible on any bubble. */
  faviconPlate: string;
  success: string;
  warning: string;
  statusWorking: string;
  /** Unread dot. The one blue in the inbox, in both themes. */
  unread: string;
  /** Working dot on a busy bot's avatar. The one green, in both themes. */
  workingDot: string;
  overlay: string;
  scrollbar: string;
  scrollbarHover: string;
};

export const darkTokens = {
  background: "#141414",
  dialog: "#1E1E1E",
  foreground: "#FFFFFF",
  card: "#202020",
  cardForeground: "#FFFFFF",
  popover: "#202020",
  popoverForeground: "#FFFFFF",
  glassFill: "#2C2C2E",
  glassStroke: "#FCFCFC26",
  primary: "#FFFFFF",
  primaryForeground: "#000000",
  secondary: "#7676803D",
  secondaryForeground: "#FFFFFF",
  chatUser: "#5C5C5C",
  chatUserForeground: "#FFFFFF",
  muted: "#202020",
  mutedForeground: "#EBEBF599",
  faintForeground: "#EBEBF573",
  accent: "#78788052",
  accentForeground: "#FFFFFF",
  destructive: "#FF9592",
  destructiveFill: "#E5484D",
  destructiveForeground: "#FFFFFF",
  border: "#FCFCFC14",
  input: "#54545899",
  ring: "#3B82F6",
  sidebar: "#1E1E1E",
  sidebarForeground: "#FFFFFF",
  sidebarBorder: "#FCFCFC14",
  sidebarAccent: "#2B2B2E",
  sidebarAccentForeground: "#FFFFFF",
  link: "#459FFE",
  faviconPlate: "#FFFFFF",
  success: "#3FA266",
  warning: "#DB941E",
  statusWorking: "#459FFE",
  unread: "#006CEB",
  workingDot: "#3FA266",
  overlay: "rgba(0, 0, 0, 0.28)",
  scrollbar: "#1E2026",
  scrollbarHover: "#2E313A",
} as const satisfies ColorTokens;

export const lightTokens = {
  background: "#FCFCFC",
  dialog: "#FFFFFF",
  foreground: "#000000",
  card: "#EFEFEF",
  cardForeground: "#000000",
  popover: "#FFFFFF",
  popoverForeground: "#000000",
  glassFill: "#FFFFFF",
  glassStroke: "#14141416",
  primary: "#000000",
  primaryForeground: "#FFFFFF",
  secondary: "#7676801F",
  secondaryForeground: "#000000",
  chatUser: "#0B0B0B",
  chatUserForeground: "#FFFFFF",
  muted: "#F2F2F1",
  mutedForeground: "#3C3C43B3",
  faintForeground: "#3C3C4394",
  accent: "#78788029",
  accentForeground: "#000000",
  destructive: "#CE2C31",
  destructiveFill: "#E5484D",
  destructiveForeground: "#FFFFFF",
  border: "#14141414",
  input: "#3C3C434A",
  ring: "#6C6C70",
  sidebar: "#F7F7F7",
  sidebarForeground: "#000000",
  sidebarBorder: "#14141414",
  sidebarAccent: "#FFFFFF",
  sidebarAccentForeground: "#000000",
  link: "#0C64C1",
  faviconPlate: "#FFFFFF",
  success: "#007041",
  warning: "#885A07",
  statusWorking: "#0C64C1",
  unread: "#006CEB",
  workingDot: "#3FA266",
  overlay: "rgba(0, 0, 0, 0.19)",
  scrollbar: "#C8C8C4",
  scrollbarHover: "#A8A8A4",
} as const satisfies ColorTokens;

/** Web and Electron only: hover, selection and layered surfaces. Mobile has no hover. */
export type WebColorTokens = ColorTokens & {
  primaryHover: string;
  primaryDisabled: string;
  borderHover: string;
  /** The page behind panels and the sidebar. */
  backgroundAlt: string;
  /** The window frame colour where the OS paints one (Windows desktop). */
  backgroundWinAlt: string;
  /** A 3% ink wash for quiet blocks inside a panel. */
  surface: string;
  surfaceHover: string;
  hover: string;
  selected: string;
  header: string;
  panel: string;
  cardSelected: string;
  popoverHeader: string;
  popoverBorder: string;
  inputFocused: string;
  inputBorder: string;
  inputBorderHover: string;
  inputBorderFocused: string;
  menu: string;
  menuHover: string;
  tab: string;
  tabActive: string;
  foregroundSubtle: string;
  foregroundSubtlest: string;
  remoteForegroundMuted: string;
  /** The blue line a panel divider shows while hovered or dragged. */
  resizeHandle: string;
};

/**
 * Web and Electron values for the tokens they draw differently from the palettes above, which
 * follow the iOS system design for mobile. Mobile reads `darkTokens` / `lightTokens`; web reads
 * `tokens.css`, rendered from these merged palettes. Surfaces are opaque; text, borders, hover
 * and selection are the ink at an opacity, so they read the same on every surface.
 */
export const webDarkOverrides = {
  background: "#161616",
  backgroundAlt: "#161616",
  backgroundWinAlt: "#2B2B2B",
  dialog: "#202020",
  foreground: "#D4D4D4",
  card: "#2B2B2B",
  cardForeground: "#D4D4D4",
  cardSelected: "#2B2B2B",
  popover: "#2B2B2B",
  popoverForeground: "#D4D4D4",
  popoverBorder: "#FFFFFF1A",
  popoverHeader: "#202020",
  primary: "#FFFFFF",
  primaryHover: "#E5E5E5",
  primaryDisabled: "#FFFFFF24",
  primaryForeground: "#000000",
  secondary: "#363636",
  secondaryForeground: "#F8F8F8",
  chatUser: "#363636",
  chatUserForeground: "#F8F8F8",
  muted: "#202020",
  mutedForeground: "#D4D4D499",
  faintForeground: "#D4D4D466",
  foregroundSubtle: "#D4D4D499",
  foregroundSubtlest: "#D4D4D44D",
  remoteForegroundMuted: "#B3B3B3",
  resizeHandle: "#38BDF8",
  accent: "#FFFFFF0D",
  accentForeground: "#D4D4D4",
  destructive: "#FF5C5C",
  destructiveFill: "#FF5C5C",
  destructiveForeground: "#FFFFFF",
  border: "#FFFFFF1A",
  borderHover: "#FFFFFF26",
  input: "#2B2B2B",
  inputBorder: "#FFFFFF1A",
  inputBorderHover: "#FFFFFF26",
  inputBorderFocused: "#FFFFFF26",
  inputFocused: "#2B2B2B",
  ring: "#FFFFFF33",
  sidebar: "#161616",
  sidebarForeground: "#D4D4D4",
  sidebarBorder: "#FFFFFF1A",
  sidebarAccent: "#FFFFFF1A",
  sidebarAccentForeground: "#F8F8F8",
  link: "#4099FF",
  success: "#46BF72",
  warning: "#FF8A30",
  statusWorking: "#4099FF",
  unread: "#4099FF",
  workingDot: "#46BF72",
  overlay: "#000000B2",
  scrollbar: "#FFFFFF26",
  scrollbarHover: "#FFFFFF4D",
  surface: "#FFFFFF0D",
  surfaceHover: "#FFFFFF1A",
  hover: "#FFFFFF0D",
  selected: "#FFFFFF1A",
  header: "#202020",
  panel: "#202020",
  menu: "#2B2B2B",
  menuHover: "#363636",
  tab: "#202020",
  tabActive: "#161616",
} as const satisfies Partial<WebColorTokens>;

export const webLightOverrides = {
  background: "#F8F8F8",
  backgroundAlt: "#F8F8F8",
  backgroundWinAlt: "#ECECEE",
  dialog: "#FFFFFF",
  foreground: "#262626",
  card: "#FFFFFF",
  cardForeground: "#262626",
  cardSelected: "#FFFFFF",
  popover: "#FFFFFF",
  popoverForeground: "#262626",
  popoverBorder: "#0D0D0D1A",
  popoverHeader: "#F8F8F8",
  primary: "#000000",
  primaryHover: "#1F1F1F",
  primaryDisabled: "#0D0D0D24",
  primaryForeground: "#FFFFFF",
  secondary: "#E6E6E6",
  secondaryForeground: "#0D0D0D",
  chatUser: "#0D0D0D",
  chatUserForeground: "#FFFFFF",
  muted: "#F0F0F0",
  mutedForeground: "#26262699",
  faintForeground: "#26262666",
  foregroundSubtle: "#26262699",
  foregroundSubtlest: "#26262666",
  remoteForegroundMuted: "#595959",
  resizeHandle: "#0EA5E9",
  accent: "#0D0D0D0D",
  accentForeground: "#0D0D0D",
  destructive: "#E03131",
  destructiveFill: "#E03131",
  destructiveForeground: "#FFFFFF",
  border: "#0D0D0D1A",
  borderHover: "#0D0D0D26",
  input: "#FFFFFF",
  inputBorder: "#0D0D0D1A",
  inputBorderHover: "#0D0D0D26",
  inputBorderFocused: "#0D0D0D26",
  inputFocused: "#FFFFFF",
  ring: "#0D0D0D26",
  sidebar: "#F0F0F0",
  sidebarForeground: "#262626",
  sidebarBorder: "#0D0D0D1A",
  sidebarAccent: "#0D0D0D0D",
  sidebarAccentForeground: "#0D0D0D",
  link: "#0066DD",
  success: "#1E8A3E",
  warning: "#E07B00",
  statusWorking: "#0066DD",
  unread: "#0066DD",
  workingDot: "#1E8A3E",
  overlay: "#0D0D0D66",
  scrollbar: "#0D0D0D26",
  scrollbarHover: "#0D0D0D4D",
  surface: "#0D0D0D08",
  surfaceHover: "#0D0D0D0D",
  hover: "#0D0D0D0D",
  selected: "#0D0D0D0D",
  header: "#FFFFFF",
  panel: "#FFFFFF",
  menu: "#FFFFFF",
  menuHover: "#F0F0F0",
  tab: "#F0F0F0",
  tabActive: "#FFFFFF",
} as const satisfies Partial<WebColorTokens>;

export const webDarkTokens: WebColorTokens = { ...darkTokens, ...webDarkOverrides };
export const webLightTokens: WebColorTokens = { ...lightTokens, ...webLightOverrides };

/** Dark palette. Prefer `tokensForAppearance` when theme-aware. */
export const tokens = darkTokens;

export const RADIUS = "0.5rem";

export const fontWeight = { regular: "400", medium: "500", semibold: "600" } as const;

/**
 * Native text styles on the system font, in points: iOS body text is 17/22. Titles use the
 * font's own line spacing, written out so they don't inherit a smaller body line height.
 */
export const typeScale = {
  title: { fontSize: 22, lineHeight: 26, fontWeight: fontWeight.semibold },
  headline: { fontSize: 19, lineHeight: 23, fontWeight: fontWeight.semibold },
  cardTitle: { fontSize: 17, lineHeight: 22, fontWeight: fontWeight.medium },
  body: { fontSize: 17, lineHeight: 22 },
  secondary: { fontSize: 15, lineHeight: 19 },
  caption: { fontSize: 14, lineHeight: 18 },
  footnote: { fontSize: 12, lineHeight: 16 },
} as const;

/**
 * Text grows with the iOS text size until body text reaches 23pt, the xxxLarge size. Larger
 * accessibility sizes keep that size, so message bubbles, rows and cards keep their layout.
 */
export const MAX_FONT_SCALE = 23 / typeScale.body.fontSize;

/** The ink of a drawn bot's eyes, the same on every body colour and in both themes. */
export const botEyeColor = "#101014";

export const botColors = [
  "#3EC5A8",
  "#F5A03C",
  "#6A6BF5",
  "#9B5CF6",
  "#3B82F6",
  "#F2622A",
  "#D9508A",
] as const;

export function isAppearancePreference(
  value: string | null | undefined,
): value is AppearancePreference {
  return value === "system" || value === "light" || value === "dark";
}

export function normalizeAppearancePreference(
  raw: string | null | undefined,
): AppearancePreference {
  return isAppearancePreference(raw) ? raw : "system";
}

export type ResolveAppearancePreferenceOptions = {
  stored?: string | null;
  storage?: Pick<Storage, "getItem"> | null;
};

function getLocalStorage(): Pick<Storage, "getItem" | "setItem"> | null {
  try {
    return typeof localStorage !== "undefined" ? localStorage : null;
  } catch {
    return null;
  }
}

export function resolveAppearancePreference(
  options: ResolveAppearancePreferenceOptions = {},
): AppearancePreference {
  const stored =
    options.stored !== undefined
      ? options.stored
      : readStoredAppearance(options.storage ?? getLocalStorage());
  return normalizeAppearancePreference(stored);
}

export function persistAppearancePreference(
  preference: AppearancePreference,
  storage: Pick<Storage, "setItem"> | null = getLocalStorage(),
): void {
  if (!storage) return;
  try {
    storage.setItem(UI_APPEARANCE_STORAGE_KEY, preference);
  } catch {
    // Ignore quota / private-mode failures; in-memory preference still applies.
  }
}

export function resolveAppearance(
  preference: AppearancePreference,
  system: ResolvedAppearance = "dark",
): ResolvedAppearance {
  if (preference === "system") return system;
  return preference;
}

export function tokensForAppearance(appearance: ResolvedAppearance): ColorTokens {
  return appearance === "light" ? lightTokens : darkTokens;
}

/** The web and Electron palette; `tokensForAppearance` is the mobile one. */
export function webTokensForAppearance(appearance: ResolvedAppearance): WebColorTokens {
  return appearance === "light" ? webLightTokens : webDarkTokens;
}

function readStoredAppearance(storage: Pick<Storage, "getItem"> | null | undefined): string | null {
  if (!storage) return null;
  try {
    return storage.getItem(UI_APPEARANCE_STORAGE_KEY);
  } catch {
    return null;
  }
}

/** `cardForeground` -> `--card-foreground` */
export function cssVariableName(token: keyof WebColorTokens): string {
  return `--${token.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`)}`;
}

function renderBlock(selector: string, colorScheme: ResolvedAppearance, palette: WebColorTokens) {
  const lines = (Object.keys(palette) as (keyof WebColorTokens)[]).map(
    (token) => `  ${cssVariableName(token)}: ${palette[token].toLowerCase()};`,
  );
  return `${selector} {\n  color-scheme: ${colorScheme};\n${lines.join("\n")}\n  --radius: ${RADIUS};\n}`;
}

/** The CSS in `tokens.css` (web and Electron). Generated from the web palettes so both stay in sync. */
export function renderTokensCss(): string {
  return `${[
    "/* Generated by `pnpm --filter @rakazo/ui-tokens generate`. Edit src/index.ts instead. */",
    renderBlock(':root,\n[data-theme="dark"]', "dark", webDarkTokens),
    renderBlock('[data-theme="light"]', "light", webLightTokens),
  ].join("\n\n")}\n`;
}
